import { useProfile } from "@/context/ProfileContext";
import { useEffect, useState } from "react";
import { AlertTriangle, BellRing, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const todayIso = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const AlertRow = ({ a, onDone }) => {
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); onDone(a); } catch (e) { toast.error(formatApiError(e)); } setBusy(false); };
  const remind = () => act(() => http.post(`/assessment/students/${a.student_id}/remind`), `Reminder sent to ${a.name}`);
  const review = () => act(() => Promise.all((a.weak.length ? a.weak.slice(0, 2) : [a.subject]).map((t) =>
    http.post(`/planner/students/${a.student_id}/items`, { date: todayIso(), subject_id: a.subject_id, topic: `Review: ${t}`, note: "From the check-in" })))
    .then(() => http.post(`/assessment/students/${a.student_id}/review-assigned`, { subject_id: a.subject_id })), `Review added to ${a.name}'s plan for today`);
  const key = `${a.kind}-${a.student_id}-${a.subject_id || ""}`;
  return (
    <li className="rounded-2xl bg-white border border-[#E6DEC3] p-4 flex flex-wrap items-center gap-3" data-testid={`assess-alert-${key}`}>
      <AlertTriangle className={`w-5 h-5 shrink-0 ${a.kind === "below" ? "text-[#C87D53]" : "text-[#F2C14E]"}`} />
      <p className="flex-1 min-w-[200px] text-sm text-stone-700">{a.text}</p>
      {a.kind === "below"
        ? <button disabled={busy} onClick={review} data-testid={`assess-review-${key}`} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[#2C4A3E] text-white text-sm"><ClipboardList className="w-4 h-4" /> Assign review first</button>
        : <button disabled={busy} onClick={remind} data-testid={`assess-remind-${key}`} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[#C87D53] text-white text-sm"><BellRing className="w-4 h-4" /> Remind</button>}
    </li>
  );
};

export const useAssessmentAlerts = (classId) => {
  const [alerts, setAlerts] = useState([]);
  useEffect(() => { http.get("/assessment/alerts", { params: classId ? { class_id: classId } : {} }).then((r) => setAlerts(r.data)).catch(() => {}); }, [classId]);
  const resolve = (a) => setAlerts((x) => x.filter((y) => y !== a));
  return [alerts, resolve];
};

export const AlertList = ({ alerts, resolve, testId = "assess-alerts-list" }) => alerts.length > 0 && (
  <ul className="space-y-2" data-testid={testId}>{alerts.map((a) => <AlertRow key={`${a.kind}-${a.student_id}-${a.subject_id || ""}`} a={a} onDone={resolve} />)}</ul>
);

export const AssessmentAlertsPopup = ({ storageKey: base }) => {
  const { profile } = useProfile();
  const storageKey = `${base}:${profile?.id}`;
  const [alerts, resolve] = useAssessmentAlerts();
  const [open, setOpen] = useState(false);
  const day = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  useEffect(() => { if (alerts.length && localStorage.getItem(storageKey) !== day) setOpen(true); }, [alerts.length, storageKey, day]);
  const close = () => { localStorage.setItem(storageKey, day); setOpen(false); };
  return (
    <>
      {alerts.length > 0 && <button onClick={() => setOpen(true)} data-testid="assess-alerts-open" className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FBF3E6] border border-[#C87D53]/40 text-[#8A4F2C] text-sm"><AlertTriangle className="w-4 h-4" /> {alerts.length} check-in alert{alerts.length > 1 ? "s" : ""}</button>}
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="bg-[#FDFBF7] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="assess-alerts-popup">
          <DialogTitle className="font-display text-2xl text-[#2C4A3E]">Before you assign lessons</DialogTitle>
          <p className="text-sm text-stone-600 -mt-1">Check-in results tailor every lesson. Here's what needs attention.</p>
          <AlertList alerts={alerts} resolve={resolve} />
          {!alerts.length && <p className="text-sm text-stone-500" data-testid="assess-alerts-clear">All caught up!</p>}
          <button onClick={close} data-testid="assess-alerts-close" className="mt-2 self-start px-5 py-2.5 rounded-full border border-[#E6DEC3] text-sm">Got it</button>
        </DialogContent>
      </Dialog>
    </>
  );
};
